import { jwtDecode } from 'jwt-decode';

import { ENV_CONFIG } from '@/constants/env-config';

export type DataRegion = 'us' | 'eu';

type ApiJwtPayload = {
  data_region?: string;
};

let regionOverride: DataRegion | null = null;

export function setRegionOverride(region: DataRegion | null): void {
  regionOverride = region;
}

function getActiveEnvConfig() {
  const appEnvironment = import.meta.env.VITE_APP_ENVIRONMENT;

  if (appEnvironment === 'development') {
    return ENV_CONFIG.dev;
  }
  if (appEnvironment === 'production') {
    return ENV_CONFIG.prod;
  }

  // VITE_APP_ENVIRONMENT is unset or unrecognized. The Vite dev server must
  // never fall back to production hosts (that would hand out production
  // integration links, e.g. the Slack install URL from /setup/slack).
  return import.meta.env.DEV ? ENV_CONFIG.dev : ENV_CONFIG.prod;
}

export function normalizeRequestPath(url: string): string {
  if (!url) {
    return '';
  }
  const withoutQuery = url.includes('?') ? url.split('?')[0] : url;
  if (withoutQuery.startsWith('http')) {
    try {
      return new URL(withoutQuery).pathname;
    } catch {
      return withoutQuery;
    }
  }
  return withoutQuery;
}

export function isOrganizationRegistrationRequest(
  method: string | undefined,
  url: string | undefined
): boolean {
  if (!method || !url) {
    return false;
  }
  if (method.toUpperCase() !== 'POST') {
    return false;
  }
  return normalizeRequestPath(url) === '/organization';
}

export function getDataRegionFromAccessToken(token: string | null | undefined): DataRegion {
  if (!token) {
    return 'us';
  }
  try {
    const decoded = jwtDecode<ApiJwtPayload>(token);
    const raw = decoded.data_region;
    if (raw === 'eu' || raw === 'us') {
      return raw;
    }
    return 'us';
  } catch {
    return 'us';
  }
}

export function resolveApiBaseUrl(params: {
  accessToken: string | null | undefined;
  method: string | undefined;
  url: string | undefined;
}): string {
  const { accessToken, method, url } = params;
  const env = getActiveEnvConfig();

  if (isOrganizationRegistrationRequest(method, url)) {
    return env.global;
  }

  const region = regionOverride ?? getDataRegionFromAccessToken(accessToken ?? null);
  return env.regional[region];
}

export function getDefaultRegionalBaseUrl(): string {
  return getActiveEnvConfig().regional.us;
}
